import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  extractXmlProps,
  IFormsProvider,
  ResourceData,
  ResourceState,
  TimestampHelper,
  xmlElementToString
} from '@lfw/roesberg.liveforms.core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { WebcamImage } from 'ngx-webcam';
import { firstValueFrom } from 'rxjs';
import {
  IMAGE_COMPRESSION_SCALE_STEP,
  INTERACTIVE_DIALOG_SIZE_STYLES,
  MB,
  PHOTO_PREVIEW_DIALOG_SIZE_STYLES
} from '../../../../utils/constants';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { PhotoPreviewDialogComponent } from '../../../shared/dialogs/photo-preview-dialog/photo-preview-dialog.component';
import { WebcamCaptureDialogComponent } from '../../../shared/dialogs/webcam-capture-dialog/webcam-capture-dialog.component';
import { EditorDialogService } from '../../../shared/services/editor-dialog.service';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

interface GalleryItem {
  resourceData: ResourceData;
  src?: string;
  loaded: boolean;
}

interface PhotoItemProperties {
  text: string;
  userName: string;
  addedWhileProcessing: string;
  resultValue: string;
  resultUser: string;
  resultTime: string;
  style: string;
  prefix: string;
}

const ALLOWED_FILE_FORMATS = ['png', 'jpeg'];

@Component({
  selector: 'lfe-photo-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './photo-item-form.component.html',
  standalone: true,
  styleUrl: './photo-item-form.component.css'
})
export class PhotoItemFormComponent
  extends InteractiveItemComponent<PhotoItemProperties>
  implements OnInit, AfterViewInit
{
  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly dialog = inject(MatDialog);
  private readonly editorDialogService = inject(EditorDialogService);

  readonly resourceDataChanged = output<{ photoItemId: string; resourceData: ResourceData[] }>();

  readonly formsProvider = inject(IFormsProvider);
  readonly deviceDetector = inject(DeviceDetectorService);
  readonly translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;

  galleryItems = signal<GalleryItem[]>([]);
  readonly orderId = input.required<string>();
  loadedGalleryItems = signal<GalleryItem[]>([]);
  erroredGalleryItems = signal<GalleryItem[]>([]);
  initialValue = '';
  itemsLoadingCount = signal(0);
  showPhotoMenu = signal(false);
  isGalleryFirstLoaded = signal(false);

  isCommentPhotoItem = computed(() => this.properties.addedWhileProcessing === 'True');
  isDesktopDevice = computed(() => this.deviceDetector.isDesktop());

  displayGalleryItems = computed(() =>
    this.galleryItems().filter((item) =>
      [ResourceState.Unchanged, ResourceState.New, ResourceState.Uploaded, ResourceState.PendingUpload].includes(
        item.resourceData.state
      )
    )
  );

  innerValue = computed(() => {
    return [this.orderId(), ...this.displayGalleryItems().map((item) => `${item.resourceData.fileId}`)]
      .filter(Boolean)
      .join('|');
  });

  userNameTrimmed = computed(() => this.properties.userName.trim());

  private readonly maxUploadSizeBytes = computed(() => this.settings().maxImageUploadSize * MB);

  properties: InteractiveItemProperties & PhotoItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    userName: '',
    addedWhileProcessing: 'False',
    style: 'Normal',
    prefix: ''
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);
  }

  ngAfterViewInit(): void {
    this.loadImages();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & PhotoItemProperties {
    const props = extractXmlProps(xml);

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      customId: props['CustomID'],
      activationRule: props['ActivationRule'] || '',
      resultValue: props['ResultValue'] || '',
      resultUser: props['ResultUser'] || '',
      resultTime: props['ResultTime'] || '',
      text: props['Text'] || '',
      userName: props['UserName'] || '',
      addedWhileProcessing: props['AddedWhileProcessing'] || '',
      style: props['Style'] || 'Normal',
      prefix: props['Prefix'] || '',
      isGenericRepeatItem
    };
  }

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `photo_${this.currentId()}`);

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.innerValue() !== this.initialValue) {
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  onPhotoMenuClick(e: Event): void {
    e.preventDefault();
    this.showPhotoMenu.set(!this.showPhotoMenu());
  }

  onSelectFileClick(e: Event): void {
    e.preventDefault();
    this.showPhotoMenu.set(false);
    this.fileInput()?.nativeElement.click();
  }

  onPhotoOpenClick(e: Event): void {
    e.preventDefault();
    this.fileInput()?.nativeElement.click();
  }

  async onFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    const [_, fileFormat] = file.type.split('/');

    if (!ALLOWED_FILE_FORMATS.includes(fileFormat)) {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.unsupported-format')
      );
      return;
    }

    let processedFile: Blob;
    try {
      processedFile = await this.compressImageToLimit(file, file.type);
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.compression-failed')
      );
      return;
    }

    const resource: ResourceData = {
      data: processedFile,
      fileId: `${this.properties.prefix || ''}${TimestampHelper.getTimestamp()}.${fileFormat}`,
      state: ResourceState.New,
      saveId: '',
      orderId: this.orderId()
    };

    let src: string;
    try {
      src = await this.blobToBase64(processedFile);
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.processing-failed')
      );
      return;
    }

    try {
      await this.formsProvider.saveFormResource(resource);
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.processing-failed')
      );
      return;
    }

    const newGallery = [...this.galleryItems(), { resourceData: resource, src, loaded: true }];
    this.galleryItems.set(newGallery);
    this.resourceDataChanged.emit({
      photoItemId: this.currentId(),
      resourceData: newGallery.map((g) => g.resourceData)
    });
    this.refreshStoreData();
  }

  private refreshStoreData(): void {
    this.properties.resultValue = this.innerValue();
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
    this.formControl?.markAsDirty();
  }

  protected async compressImageToLimit(
    blob: Blob,
    mimeType: string,
    limit = this.settings().maxImageUploadSize * MB
  ): Promise<Blob> {
    if (blob.size <= limit) {
      return blob;
    }

    const bitmap = await createImageBitmap(blob);
    try {
      let width = bitmap.width;
      let height = bitmap.height;
      let compressed = blob;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;

      while (compressed.size > limit) {
        width = Math.floor(width * IMAGE_COMPRESSION_SCALE_STEP);
        height = Math.floor(height * IMAGE_COMPRESSION_SCALE_STEP);

        if (width < 1 || height < 1) {
          throw new Error('Image cannot be compressed to fit within the size limit');
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(bitmap, 0, 0, width, height);

        compressed = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('Canvas toBlob failed'));
          }, mimeType);
        });
      }

      return compressed;
    } finally {
      bitmap.close();
    }
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const cleanup = () => {
        reader.onload = null;
        reader.onerror = null;
      };
      reader.onload = () => {
        cleanup();
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        cleanup();
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  async loadImages(): Promise<void> {
    const loadedFileIds = this.properties.resultValue.split('|').filter((item) => item.includes('.'));
    this.itemsLoadingCount.set(loadedFileIds.length);
    this.isGalleryFirstLoaded.set(false);

    const resources = loadedFileIds.map((fileId) => {
      const resource: ResourceData = {
        data: new Blob(),
        fileId: fileId,
        state: ResourceState.Unchanged,
        saveId: '',
        orderId: this.orderId()
      };
      return resource;
    });

    const galleryItems = await Promise.all(
      resources.map(async (item) => {
        const galleryItem: GalleryItem = { resourceData: item, loaded: false };
        try {
          galleryItem.resourceData.data = await firstValueFrom(this.formsProvider.loadFormResource(item.fileId));
          if (galleryItem.resourceData.data) {
            const base64 = await this.blobToBase64(galleryItem.resourceData.data);
            galleryItem.src = base64;
            galleryItem.loaded = true;
          }
        } catch (error) {
          console.error('Failed to load image:', item.fileId, error);
        }
        return galleryItem;
      })
    );

    const newGallery = [...this.galleryItems(), ...galleryItems];
    this.galleryItems.set(newGallery);
    this.isGalleryFirstLoaded.set(true);
    this.resourceDataChanged.emit({
      photoItemId: this.currentId(),
      resourceData: newGallery.map((g) => g.resourceData)
    });
  }

  setItemAsLoaded(item: GalleryItem): void {
    this.loadedGalleryItems.update((values) => {
      values.push(item);
      return values;
    });
  }

  setItemAsErrored(item: GalleryItem): void {
    this.erroredGalleryItems.update((values) => {
      values.push(item);
      return values;
    });
  }

  isLoaded(item: GalleryItem) {
    return this.loadedGalleryItems().find((i) => i === item);
  }

  isErrored(item: GalleryItem) {
    return this.erroredGalleryItems().find((i) => i === item);
  }

  onThumbnailClick(e: Event, item: GalleryItem): void {
    e.preventDefault();

    const dialogRef = this.dialog.open(PhotoPreviewDialogComponent, {
      ...PHOTO_PREVIEW_DIALOG_SIZE_STYLES,
      data: {
        title: this.properties.text,
        imageSrc: item.src || '',
        imageAlt: item.resourceData.fileId || '',
        canDelete: !!this.formControl?.enabled
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: string | undefined) => {
        if (result === 'delete') {
          this.deletePhoto(item);
        }
      });
  }

  private deletePhoto(item: GalleryItem): void {
    const state = item.resourceData.state;

    switch (state) {
      case ResourceState.Deleted:
        console.error('Cannot delete a deleted photo');
        break;
      case ResourceState.Unchanged:
      case ResourceState.New:
      case ResourceState.Uploaded:
      case ResourceState.PendingUpload: {
        const newGallery = this.galleryItems().map((galleryItem) => {
          if (galleryItem.resourceData.fileId === item.resourceData.fileId) {
            galleryItem.resourceData.state = ResourceState.Deleted;
          }
          return galleryItem;
        });
        this.galleryItems.set(newGallery);
        this.resourceDataChanged.emit({
          photoItemId: this.currentId(),
          resourceData: newGallery.map((g) => g.resourceData)
        });
        this.refreshStoreData();
        break;
      }
      default: {
        console.warn(`Unhandled ResourceState for delete photo click: ${state}`);
      }
    }
  }

  onCaptureClick(e: Event): void {
    e.preventDefault();
    this.showPhotoMenu.set(false);

    const dialogRef = this.dialog.open(WebcamCaptureDialogComponent, {
      ...INTERACTIVE_DIALOG_SIZE_STYLES,
      disableClose: false,
      data: {
        title: this.translateService.instant('liveforms-editor.photo-item.webcam.title')
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((webcamImage: WebcamImage | undefined) => {
        if (webcamImage) {
          void this.processWebcamPhoto(webcamImage);
        }
      });
  }

  private async processWebcamPhoto(webcamImage: WebcamImage): Promise<void> {
    const base64Data = webcamImage.imageAsDataUrl;

    let blob: Blob;
    try {
      blob = await this.base64ToBlob(base64Data);
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.processing-failed')
      );
      return;
    }

    let processedBlob: Blob;
    try {
      processedBlob = await this.compressImageToLimit(blob, 'image/png');
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.compression-failed')
      );
      return;
    }

    const resource = new ResourceData();
    resource.data = processedBlob;
    resource.fileId = `${this.properties.prefix || ''}${TimestampHelper.getTimestamp()}.png`;
    resource.state = ResourceState.New;
    resource.orderId = this.orderId();
    resource.saveId = '';

    try {
      await this.formsProvider.saveFormResource(resource);
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.processing-failed')
      );
      return;
    }

    let src: string;
    try {
      src = processedBlob === blob ? base64Data : await this.blobToBase64(processedBlob);
    } catch {
      await this.editorDialogService.showErrorDialog(
        this.translateService.instant('liveforms-editor.photo-item.error.header'),
        this.translateService.instant('liveforms-editor.photo-item.error.processing-failed')
      );
      return;
    }

    const newGallery = [...this.galleryItems(), { resourceData: resource, src, loaded: true }];
    this.galleryItems.set(newGallery);
    this.resourceDataChanged.emit({
      photoItemId: this.currentId(),
      resourceData: newGallery.map((g) => g.resourceData)
    });

    this.refreshStoreData();
  }

  private async base64ToBlob(base64: string): Promise<Blob> {
    const response = await fetch(base64);
    return response.blob();
  }

  protected readonly Array = Array;
}
